import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Position } from '../../models/position';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { WebviewService } from './webview.service';
import { WidgetService } from './widget.service';

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  private watchPositionStatus: boolean = false;
  private watchPositionRef: number = 0;
  private lastPosition: Position | null = null;
  public ObservableLastPosition = new BehaviorSubject<Position | null>(null);
  private fakeGPSReplayMode: boolean = false;

  public readonly webviewService = inject(WebviewService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);
  private readonly widgetService = inject(WidgetService);

  constructor() {
    if (globalThis.navigator.userAgent.includes("FB_IAB")) {
      this.initFBLocation();
    }
  }

  public initWebViewLocation(): void {
    this.webviewService.InitLocationFunction((...location) => {
      const [timestamp, latitude, longitude, altitude, accuracy, altitudeAccuracy, heading, speed] = location;
      if (this.fakeGPSReplayMode) {
        return;
      }
      if (latitude == null && longitude == null) {
        this.lastPosition = null;
      }
      else {
        this.setLastPosition(new Position({ latitude, longitude, speed, accuracy, altitude, altitudeAccuracy, heading, timestamp }))
      }
    });
  }

  private initFBLocation(): void {
    this.startWatchPosition();
  }

  startWatchPosition() {
    // Widget mode: the browser is never asked for geolocation (no permission prompt).
    // Positions arrive from the embedding page via WidgetApiService.gpsPositionUpdate.
    if (this.widgetService.isWidget()) return;
    if (!this.watchPositionStatus) {
      if (this.isTeslaBrowser()) {
        if (navigator.geolocation) {
          this.watchPositionStatus = true;
          this.getPositionLoop();
        }
      }
      else {
        this.watchPositionRef = navigator.geolocation.watchPosition(
          (position) => {
            if (this.fakeGPSReplayMode) {
              return;
            }
            if (this.lastPosition === null || Date.now() - this.lastPosition.Timestamp > 800) {
              this.setLastPosition(new Position({
                latitude: position.coords.latitude, longitude: position.coords.longitude, speed: position.coords.speed ?? 0,
                accuracy: position.coords.accuracy, altitude: position.coords.altitude ?? 0,
                altitudeAccuracy: position.coords.altitudeAccuracy ?? 0, heading: position.coords.heading ?? 0,
                timestamp: position.timestamp
              }));
            }
          },
          (error) => {
          },
          { enableHighAccuracy: true, timeout: 1000, maximumAge: 0 });

        this.watchPositionStatus = true;
      }
    }
  }

  private getPositionLoop(): void {
    if (this.watchPositionStatus) {
      navigator.geolocation.getCurrentPosition((e) => {
        this.gpsPositionSuccess(e);
      }, () => { }, { enableHighAccuracy: false, maximumAge: 900, timeout: 900 });
      setTimeout(() => { this.getPositionLoop() }, 1000);
    }
  }

  private gpsPositionSuccess(position: any): void {
    if (this.fakeGPSReplayMode) {
      return;
    }
    if (position?.coords) {
      this.setLastPosition(new Position({
        latitude: position.coords.latitude, longitude: position.coords.longitude, speed: position.coords.speed,
        accuracy: position.coords.accuracy, altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy, heading: position.coords.heading, timestamp: position.timestamp
      }));
    }
  }

  public stopWatchPosition(): void {
    this.watchPositionStatus = false;
  }

  public getLastPosition(): Position | null {
    return this.lastPosition;
  }

  public setLastPosition(position: Position): void {
    this.lastPosition = position;
    this.ObservableLastPosition.next(position);
  }

  public getWatchPositionStatus(): boolean {
    return this.watchPositionStatus;
  }

  public setFakeGPSReplayMode(fakeGPS: boolean): void {
    this.fakeGPSReplayMode = fakeGPS;
  }

  public getFakeGPSReplayMode(): boolean {
    return this.fakeGPSReplayMode;
  }

  private isTeslaBrowser(): boolean {
    const isChromeVersionLessThan = (userAgent: string, version: number): boolean => {
      const match = /Chrome\/(\d+(\.\d+)+)/.exec(userAgent);
      return match !== null && Number.parseFloat(match[1]) < version;
    };

    const isLinux64UserAgent = (userAgent: string): boolean => userAgent.includes("X11; Linux x86_64");

    const isSpecialBrowser = (userAgent: string): boolean => userAgent.includes("QtCarBrowser") || userAgent.includes("Tesla");

    const userAgent = navigator.userAgent;
    const isAffectedVersion = isLinux64UserAgent(userAgent) && isChromeVersionLessThan(userAgent, 115);

    const result = isSpecialBrowser(userAgent) || isAffectedVersion;
    return result;
  }
}
